import type { Brand } from '@zg-rentals/multibrand-support';
import { List } from '@zillow/constellation-10';
import {
  AutoResponseMessage,
  HighPrioritySystemMessage,
  LowPrioritySystemMessage,
  MESSAGE_STATUS,
  MESSAGE_TYPES,
  type MessageStatusVariant,
} from '@zg-rentals/satellite-components';
import { logAppError } from '../../utils/errorUtils';
import ChatDisabledMessage from './components/ChatDisabledMessage';
import ListingMessageItem from './components/ListingMessageItem';
import PhoneLead from './components/PhoneLead';
import useGetFeatureFlagsAndSettings from '../../hooks/useGetFeatureFlagsAndSettings';
import type { Folder } from '../../types/GraphqlTypes';
import { MessageList_Rh_GetConversationDocument, SatelliteAiAutoResponseType } from '../../types/GraphqlTypes';
import { useQueryClient } from '@zg-rentals/react-query5';
import { SatelliteWebRenterConversationHandler_getLatestConversations_Path } from '@zg-rentals/ts-satellite-client';
import { mapFolderToApiType } from '../../utils/folderUtils';
import type { AttachmentsForMessage } from '../MessageAttachments';
import type { DecoratedMessage } from '../MessageList';
import ConfigConstants from '../../constants/ConfigConstants';
import { useSatelliteWebRenterConversationHandler_addMessage } from '@zg-rentals/ts-satellite-client-react-query';
import { useRefetchQueriesWithRetry } from '../../hooks/useRefetchQueriesWithRetry';

export enum MessageTypes {
  NotAcceptingReplies = 'notAcceptingReplies',
  PhoneLead = 'phoneLead',
  ListingMessage = 'listingMessage',
  SystemMessage = 'systemMessage',
}

type Props = {
  brand: Brand;
  conversationId: string;
  isLastMessageInList: boolean;
  message: DecoratedMessage;
  messageKey: string;
  selectedFolder: Folder;
  sendStatus?: MessageStatusVariant;
  shouldFetchOlderConversations: boolean;
  updateTemporaryMessage(messageKey: string, updates: Partial<DecoratedMessage>): void;
};

const getAttachmentZDocIdsForAddMessageV2Mutation = (attachments: AttachmentsForMessage): Array<string> => {
  return attachments.map(({ zDocId }) => zDocId || '');
};

const MessageItem = ({
  brand,
  conversationId,
  isLastMessageInList,
  message: messageObject,
  messageKey,
  selectedFolder,
  sendStatus = MESSAGE_STATUS.SENT,
  shouldFetchOlderConversations,
  updateTemporaryMessage,
}: Props) => {
  const {
    aiAutoResponseType,
    attachments,
    isMessageOwner,
    message: messageContent = '',
    messageDateMs = '',
    messageLink,
    messageId = '',
    messageType,
    senderName = '',
    title = '',
    uiTreatmentType,
  } = messageObject;

  const { mutateAsync: addMessage } = useSatelliteWebRenterConversationHandler_addMessage();
  const queryClient = useQueryClient();
  const refetchQueries = useRefetchQueriesWithRetry({
    errorContext: 'component.MessageItem.handleResendMessage',
  });
  const { features } = useGetFeatureFlagsAndSettings();
  const { enableMessageLink } = features;
  const ctaText = messageLink?.text ?? '';
  const systemMessageUrl = messageLink?.url ?? '';
  const isHighPrioritySystemMessage =
    uiTreatmentType === MESSAGE_TYPES.HIGH_PRIORITY || uiTreatmentType === MESSAGE_TYPES.HIGH_PRIORITY_WITH_LOGO;
  const shouldDisplaySentStatus = !!messageDateMs && isLastMessageInList && !!isMessageOwner;

  const handleSystemMessageCtaClick = () => {
    return window.open(systemMessageUrl, '_newtab');
  };

  const renderMessage = () => {
    if (aiAutoResponseType === SatelliteAiAutoResponseType.EliseAi && messageContent) {
      return (
        <AutoResponseMessage
          enableClickableLinks={!!enableMessageLink}
          message={messageContent}
          messageDateMs={messageDateMs}
          metaLabelType="eliseAI"
        />
      );
    }

    if (aiAutoResponseType === SatelliteAiAutoResponseType.ValAi && messageContent) {
      return (
        <AutoResponseMessage
          enableClickableLinks={!!enableMessageLink}
          message={messageContent}
          messageDateMs={messageDateMs}
          metaLabelType="valAI"
        />
      );
    }

    switch (messageType) {
      case MessageTypes.ListingMessage:
        return (
          <ListingMessageItem
            attachments={attachments}
            conversationId={conversationId}
            handleResendMessage={handleResendMessage}
            isMessageOwner={!!isMessageOwner}
            messageContent={messageContent!}
            messageDateMs={messageDateMs}
            messageId={messageId!}
            senderName={senderName!}
            sendStatus={sendStatus}
            shouldDisplaySentStatus={shouldDisplaySentStatus}
          />
        );
      case MessageTypes.PhoneLead:
        return (
          <PhoneLead isMessageOwner={!!isMessageOwner} messageContent={messageContent!} messageDateMs={messageDateMs} />
        );

      case MessageTypes.NotAcceptingReplies:
        return <ChatDisabledMessage brand={brand} message={messageContent || ''} />;
      default:
        return (
          <>
            {isHighPrioritySystemMessage ? (
              <HighPrioritySystemMessage
                ctaText={ctaText}
                onCtaClick={handleSystemMessageCtaClick}
                message={messageContent ?? ''}
                messageDateMs={messageDateMs}
                messageType={messageType}
                persona="renter"
                title={title ?? ''}
                uiTreatmentType={uiTreatmentType!}
              />
            ) : (
              <LowPrioritySystemMessage
                ctaText={ctaText}
                message={messageContent ?? ''}
                messageDateMs={messageDateMs}
                url={systemMessageUrl}
              />
            )}
          </>
        );
    }
  };

  const handleResendMessage = async () => {
    try {
      updateTemporaryMessage(messageKey, { sendStatus: MESSAGE_STATUS.SENDING });
      const zDocIds = attachments.length > 0 ? getAttachmentZDocIdsForAddMessageV2Mutation(attachments) : [];

      const result = await addMessage({
        postData: {
          conversationId,
          message: messageContent!,
          uploadedZdocIds: zDocIds,
        },
      });

      if (result) {
        const { messageId, messageDateMs } = result;

        //update temporary msgs array if resend successful
        updateTemporaryMessage(messageKey, {
          messageId: messageId,
          sendStatus: MESSAGE_STATUS.SENT,
          messageDateMs: String(messageDateMs),
        });

        // Refetch MessageList (Apollo). Invalidate conversation list (React Query) so preview updates.
        refetchQueries([
          {
            query: MessageList_Rh_GetConversationDocument,
            variables: { conversationId },
            name: 'MessageList',
          },
        ]);
        queryClient.invalidateQueries({
          queryKey: [
            SatelliteWebRenterConversationHandler_getLatestConversations_Path,
            {
              folder: mapFolderToApiType(selectedFolder),
              daysBack: shouldFetchOlderConversations ? ConfigConstants.MAX_DAYS_BACK_FOR_CONVERSATIONS : undefined,
            },
          ],
        });
      } else {
        throw new Error();
      }
    } catch (error) {
      logAppError({
        context: { errorLocation: 'component.MessageItem.handleResendMessage' },
        message: error.message,
        error,
      });

      updateTemporaryMessage(messageKey, { sendStatus: MESSAGE_STATUS.FAILED });
    }
  };

  return (
    <List.Item
      aria-roledescription="message"
      aria-live={isLastMessageInList ? 'polite' : 'off'}
      key={messageKey}
      css={{
        listStyle: 'none',
        marginLeft: isMessageOwner ? 'auto' : 0,
        marginBlock: 'default',
        paddingBlock: 'none',
      }}
    >
      {renderMessage()}
    </List.Item>
  );
};

export default MessageItem;
